import dayjs from 'dayjs';

const DATE_TIME_FORMAT = 'MMM D, YYYY h:mm A';
const TIME_FORMAT = 'h:mm A';

/**
 * The attendance-list header's "when" line: `Aug 13, 2026 9:00 AM - 11:00 AM MT`.
 *
 * Start and end are always on the same calendar day, so the date is not repeated.
 * Degrades rather than printing something wrong, matching the backend's
 * SessionDateTimeFormatter so this page agrees with the email that linked here (GROW-4225):
 * - no usable end → the start alone, still with the zone;
 * - no timezone (a zone the backend cannot label MT/PT) → no suffix, rather than a
 *   label that would misstate the time;
 * - no start → null, letting the caller fall back to its own date-only formatting.
 *
 * The end is also dropped when it is not strictly after the start. The backend guards this
 * too, but an inverted range is a reachable state on a published workshop (GROW-4212) and
 * "2:00 PM - 10:00 AM" reads backwards, so the check is kept on both sides.
 *
 * @param start ISO date-time from the API, e.g. `2026-08-13T09:00:00`
 * @param end   ISO date-time from the API; nullable for legacy parts with no end time
 * @param timezone `MT`, `PT`, or nullish
 */
export const formatSessionDateTimeRange = (
  start?: string | null,
  end?: string | null,
  timezone?: string | null,
): string | null => {
  if (!start) return null;

  const startAt = dayjs(start);
  if (!startAt.isValid()) return null;

  const endAt = end ? dayjs(end) : null;
  const label =
    endAt?.isValid() && endAt.isAfter(startAt)
      ? `${startAt.format(DATE_TIME_FORMAT)} - ${endAt.format(TIME_FORMAT)}`
      : startAt.format(DATE_TIME_FORMAT);

  return timezone ? `${label} ${timezone}` : label;
};
